import { useState } from 'react'
import { useUpdateEffect } from 'react-use'

import { EmbroideryTypeEnum } from '../../__generated__/graphql/catalog/graphql'
import { useTranslation } from '../../app/_providers/TranslationProvider.client'

import { EmbroideryService } from './EmbroideryService'
import { EmbroideryFormValues } from './types'

export type EmbroideryConfirmationErrors = {
  [key in keyof EmbroideryFormValues]?: string
}

type UseEmbroideryConfirmationOpts = {
  /** Handler for submission of embroidery modal. Will only be called when all validation rules pass. */
  onSubmit: () => void
  /** Embroidery form options that should be considered "required" within the embroidery modal. */
  requiredEmbroideryOptions?: EmbroideryTypeEnum[]
}

/**
 * Manage the confirmation of the embroidery modal.
 *
 * This hook ensures that we do not allow for a user to confirm their embroidery selection unless
 * their embroidery selections satisfies the required validation rules defined by their portal.
 */
export function useEmbroideryConfirmation({
  onSubmit,
  requiredEmbroideryOptions = [],
}: UseEmbroideryConfirmationOpts) {
  const { selectedEmbroideryOptions, embroideryOptions } = EmbroideryService.useContainer()
  const {
    embroidery: { validation },
  } = useTranslation()

  const [formErrors, setFormErrors] = useState<EmbroideryConfirmationErrors>({})

  const handleConfirm = () => {
    const errors: EmbroideryConfirmationErrors = {}

    if (requiredEmbroideryOptions.length > 0) {
      // Set error if mandatory logo embroidery is enforced & logo embroidery was not selected
      if (requiredEmbroideryOptions.includes('LOGO_ONLY') && !selectedEmbroideryOptions.LOGO_ONLY) {
        const hasLogoOnly = embroideryOptions.some(({ kind }) => kind === 'LOGO_ONLY')
        if (hasLogoOnly) errors['LOGO_ONLY'] = validation.logoEmbroideryRequired
      }

      // Set error if mandatory text embroidery is enforced & logo embroidery was not selected
      if (requiredEmbroideryOptions.includes('TEXT_ONLY') && !selectedEmbroideryOptions.TEXT_ONLY) {
        const hasTextOnly = embroideryOptions.some(({ kind }) => kind === 'TEXT_ONLY')
        if (hasTextOnly) errors['TEXT_ONLY'] = validation.textEmbroideryRequired
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
    } else {
      onSubmit()
    }
  }

  // Clear error state whenever selected embroidery options change
  useUpdateEffect(() => {
    setFormErrors({})
  }, [selectedEmbroideryOptions])

  return { handleConfirm, formErrors }
}
